import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "../../../helpers/API/amplifyApi";
import { useAuth } from "../../../authentication/authHelpers";
import {
  canAccessOrari,
  deriveViewerRole,
} from "../utils/deriveViewerRole";

/** Module cache so Sidebar + Orari share one policies fetch per session. */
let policiesCache = null;
let policiesPromise = null;

async function loadPolicies() {
  if (policiesCache) return policiesCache;
  if (!policiesPromise) {
    policiesPromise = API.get("shiftRotationPolicies", "/shiftRotationPolicies")
      .then((res) => {
        policiesCache = Array.isArray(res) ? res : [];
        return policiesCache;
      })
      .catch(() => {
        policiesCache = [];
        return policiesCache;
      })
      .finally(() => {
        policiesPromise = null;
      });
  }
  return policiesPromise;
}

/**
 * Whether the current user should see /orari in the sidebar and be able
 * to open the page. Non-participants resolve to false (no empty-state page).
 */
export default function useOrariAccess() {
  const auth = useAuth();
  const { departments } = useSelector((state) => state.departments);
  const [policies, setPolicies] = useState(policiesCache);
  const [ready, setReady] = useState(!!policiesCache);

  useEffect(() => {
    let alive = true;
    loadPolicies().then((pol) => {
      if (!alive) return;
      setPolicies(pol);
      setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const viewer = useMemo(
    () =>
      deriveViewerRole({
        auth,
        policies: policies || [],
        departments: departments || [],
      }),
    [auth, policies, departments]
  );

  // While policies are loading, hide Orari for non-admins to avoid a flash
  // of a menu item that then disappears for Finance / non-rotation staff.
  const userRole = (auth?.userRole || auth?.user?.userRole || "").toLowerCase();
  const isAdmin =
    userRole === "admin" || userRole === "administrator";

  const allowed = isAdmin || (ready && canAccessOrari(viewer));

  return { allowed, ready, viewer };
}
